import type {
  Bundle,
  Coding,
  Reference,
  Resource,
  Task,
} from '@medplum/fhirtypes'
import { QUEUED_EDITS_ORIGIN_SYSTEM } from '#src/codeSystems.ts'
import type { ExtendedResource } from '#src/ExtendedResource.ts'
import { getReferenceString } from '#src/references.ts'
import { getTag } from '#src/tags.ts'

// The meta.tag stamping a resource with the queued-edits review record it was
// changed by — the proposal Bundle (inline ai-chat, no Task) or the
// QueuedEditsReviewTask (async/inbox). Apply it on the mutation builder (`new
// BatchMutationBuilder({ tag })`) so every entry — POST bodies and PATCH ops
// alike — carries it natively through the existing tag machinery. The code is
// the review's reference string so activity feeds can resolve and link straight
// back to it.
export function queuedEditsOriginTag(ref: Reference<Bundle | Task>): Coding {
  const reference = getReferenceString(ref)
  return {
    system: QUEUED_EDITS_ORIGIN_SYSTEM,
    code: reference,
    display: ref.display,
  }
}

// The originating queued-edits review record carried by a raw FHIR resource's
// queued-edits origin tag (a Bundle or Task reference), or undefined when
// absent. Mirrors `ExtendedResource.queuedEditsOriginTask` for callers that
// only hold a plain FHIR resource (no model wrapper) and just need the
// present-or-not backlink.
export function queuedEditsOriginTaskFromResource(
  resource: Resource
): Reference<Task | Bundle> | undefined {
  const tag = getTag(resource, QUEUED_EDITS_ORIGIN_SYSTEM)
  return tag?.code ? { reference: tag.code, display: tag.display } : undefined
}

// The originating QueuedEditsReviewTask for the change a given version
// introduced, or undefined when this version did not originate from a queued
// edit (or merely inherited the tag from an earlier version).
//
// The queued-edits origin tag persists onto every subsequent version of a
// resource once applied. To attribute the change to the review exactly once —
// on the version that actually applied it — we only report the origin when the
// current version carries the tag AND the immediately prior version did not (or
// carried a different one). Later unrelated edits keep the tag but won't
// re-surface the backlink.
export function introducedQueuedEditsOriginTask(
  current: ExtendedResource,
  previous: ExtendedResource | undefined
): Reference<Task | Bundle> | undefined {
  const currentTask = current.queuedEditsOriginTask
  if (!currentTask) {
    return undefined
  }
  const previousTask = previous?.queuedEditsOriginTask
  if (
    previousTask &&
    getReferenceString(previousTask) === getReferenceString(currentTask)
  ) {
    return undefined
  }
  return currentTask
}
